<template>
  <div data-page-name="about">
    <section class="hero">
      <h1 v-html="$t('about.hero.title')" />
      <p v-html="$t('about.hero.description')" />

      <div class="badges">
        <NuxtImg
          src="/redesign/images/badges/veeam-ready-repository-badge.webp"
          :alt="$t('Alts.Badge.veeam-ready-object')"
          width="206"
          height="200"
          sizes="xs:120px md:164px xl:206px"
          densities="x1 x2"
          format="webp"
          preload
        />

        <NuxtImg
          src="/redesign/images/badges/veeam-ready-immutability-badge.webp"
          :alt="$t('Alts.Badge.veeam-ready-repository')"
          width="340"
          height="200"
          sizes="xs:192px md:264px xl:340px"
          densities="x1 x2"
          format="webp"
          preload
        />
      </div>
    </section>

    <section class="cards">
      <section class="card white">
        <h3>{{ $t('Mission') }}</h3>
        <strong>{{ $t('about.cards.white.description') }}</strong>
      </section>

      <section class="card purple">
        <h3>{{ $t('Vision') }}</h3>
        <strong>{{ $t('about.cards.purple.description') }}</strong>
      </section>
    </section>

    <Awards />

    <section class="leadership">
      <div class="wrapper">
        <h2>{{ $t('about.leadership.title') }}</h2>

        <ul class="list">
          <Cards.Leader
            v-for="(item, index) of LEADERSHIP_ITEMS"
            v-bind="item"
            :key="index"
            tag="li"
            direction="column"
          />
        </ul>
      </div>
    </section>

    <section class="executive-board">
      <div class="wrapper">
        <h2>{{ $t('about.executive-board.title') }}</h2>

        <ul class="list">
          <Cards.Leader
            v-for="(item, index) of EXECUTIVE_BOARD_ITEMS"
            v-bind="item"
            :key="index"
            tag="li"
            direction="row"
          />
        </ul>
      </div>
    </section>

    <section class="got-veeam">
      <div class="inner">
        <div class="left" v-html="$t('about.got-veeam')" />

        <div class="right">
          <Buttons.Link
            :to="localePathWithSlash(PATHS.CONTACTS_US.path)"
            class="nowrap"
            target="_blank"
          >
            {{ $t(PATHS.CONTACTS_US.i18nKey) }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// composables
import { useI18n } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// components
import Awards from '~/components/redesign/Awards.vue';
import Cards from '~/components/redesign/Cards';
import Buttons from '~/components/redesign/Buttons';

// constants
import { PATHS } from '~/constants';

// types
import type { CardsLeader } from '~/components/redesign/Cards/Leader.vue';

const { t } = useI18n();
const localePathWithSlash = useLocalePathWithSlash();

const LEADERSHIP_ITEMS: Array<Omit<CardsLeader, 'direction' | 'tag'>> = [
  {
    avatar: '/img/template3/about/bannet3.png',
    fullName: 'David Bennett',
    position: t('position.CEO'),
    linkedIn: 'https://www.linkedin.com/in/davidbennett/',
  },
  {
    avatar: '/img/template3/about/k-v.png',
    fullName: 'Kelly Wells',
    position: t('position.Chief_Operating_Officer'),
    linkedIn: 'https://www.linkedin.com/in/kelly-titterington-wells/',
  },
  {
    avatar: '/img/template3/about/h-k.png',
    fullName: 'Harry Kurland',
    position: t('position.Vice_President_of_Sales_Americas'),
    linkedIn: 'https://www.linkedin.com/in/harrykurland/',
  },
  {
    avatar: '/redesign/images/personas/Daniel_Fried.webp',
    fullName: 'Daniel Fried',
    position: t('position.Senior_VP_of_Sales_EMEA'),
    linkedIn: 'https://www.linkedin.com/in/danielfried/',
  },
  {
    avatar: '/img/template3/about/e-r-s.png',
    fullName: 'Eric R Schott',
    position: t('position.Chief_Product_Officer'),
    linkedIn: 'https://www.linkedin.com/in/eschott/',
  },
  {
    avatar: '/img/template3/about/a-w.png',
    fullName: 'Andrew Wittman',
    position: t('position.Chief_Marketing_Officer'),
    linkedIn: 'https://www.linkedin.com/in/awittman/',
  },
];

const EXECUTIVE_BOARD_ITEMS: Array<Omit<CardsLeader, 'direction' | 'tag'>> = [
  {
    avatar: '/img/template3/about/timashev3.png',
    fullName: 'Ratmir Timashev',
    position: t('position.Co-Founder_of_Object_First'),
    bio: t('about.executive-board.timashev.bio'),
    linkedIn: 'https://www.linkedin.com/in/ratmirtimahsev/',
  },
  {
    avatar: '/img/template3/about/baranov3.png',
    fullName: 'Andrei Baronov',
    position: t('position.Co-Founder_of_Object_First'),
    bio: t('about.executive-board.baranov.bio'),
    linkedIn: 'https://www.linkedin.com/in/andrei-baronov-131a19/',
  },
];

useApiSeo('about', 'Page', {
  title: t('about.seo.title'),
  description: t('about.seo.description'),
  ogType: 'website',
});
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/variables/shadows';
@import '$/functions/token';

[data-page-name='about'] {
  padding: 5rem 0 8rem 0;

  @include mobile {
    padding: 2.5rem 0 5rem 0;
  }

  section.hero {
    @include wrapper(80rem);

    @include flex-start-start;
    flex-direction: column;
    gap: 2rem;

    margin-bottom: 7rem;
    padding: 0 2rem;

    @include tablet {
      margin-bottom: 8rem;
    }

    @include mobile {
      gap: 1.5rem;
      padding: 0 1rem;
      margin-bottom: 4.7rem;
    }

    h1 {
      @include fluid-max-width(50rem);
      @include h2;
    }

    p {
      @include fluid-max-width(50rem);
      @include body-1;
    }

    .badges {
      @include flex-start-start;
      flex-direction: row;
      gap: 2rem;

      @include mobile {
        gap: 0.5rem;
      }

      img {
        &:first-of-type {
          max-width: 12.875rem;
          height: auto;

          @include tablet {
            max-width: 10.25rem;
          }

          @include mobile {
            max-width: 7.5rem;
          }
        }

        &:last-of-type {
          max-width: 21.25rem;
          height: auto;

          @include tablet {
            max-width: 16.5rem;
          }

          @include mobile {
            max-width: 12rem;
          }
        }
      }
    }
  }

  section.cards {
    @include wrapper(80rem);

    @include flex-stretch-sb;
    gap: 2rem;
    margin-bottom: 10rem;

    padding: 0 2rem;

    @include tablet {
      gap: 1.5rem;
      margin-bottom: 8rem;
    }

    @include mobile {
      flex-direction: column;
      margin-bottom: 5rem;
      padding: 0 1rem;
    }

    section.card {
      width: 100%;
      padding: 2rem;

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }

      h3 {
        @include h4;
        margin-bottom: 1rem;
      }

      strong {
        @include subtitle-1;
      }

      &.white {
        color: token('text-primary');
        background-color: token('surf-cont-primary');
        box-shadow: map-get($shadows, 'Elevation 3');
      }

      &.purple {
        color: token('text-reversed');
        background-color: token('surf-cont-secon-action');
      }
    }
  }

  section.leadership {
    @include wrapper(80rem);

    margin-top: 8rem;
    padding: 0 2rem;

    @include tablet {
      margin-top: 4rem;
    }

    @include mobile {
      margin: 5rem 0;
      padding: 0 1rem;
    }

    h2 {
      @include h3;
      margin-bottom: 5rem;

      @include tablet {
        margin-bottom: 4rem;
      }

      @include mobile {
        margin-bottom: 2.5rem;
      }
    }

    ul.list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(24rem, 1fr));
      grid-gap: 2rem;

      @include tablet {
        grid-template-columns: repeat(auto-fit, minmax(21.25rem, 1fr));
        grid-gap: 1.5rem;
      }

      @include mobile {
        grid-template-columns: 1fr;
      }
    }
  }

  section.executive-board {
    @include wrapper(80rem);

    margin-top: 8rem;
    padding: 0 2rem;

    @include tablet {
      margin-top: 4rem;
    }

    @include mobile {
      margin-top: 2rem;
      padding: 0 1rem;
    }

    .wrapper {
      @include fluid-max-width(50rem);
      margin-bottom: 8rem;

      @include tablet {
        margin-bottom: 8rem;
      }

      @include mobile {
        margin-bottom: 5rem;
      }

      h2 {
        @include h3;
        margin-bottom: 5rem;

        @include tablet {
          margin-bottom: 4rem;
        }

        @include mobile {
          margin-bottom: 1.5rem;
        }
      }

      ul.list {
        @include flex-start-start;
        flex-direction: column;
        gap: 2rem 0;

        @include tablet {
          gap: 1.5rem 0;
        }
      }
    }
  }

  section.got-veeam {
    @include wrapper(80rem);

    padding: 0 2rem;

    @include mobile {
      padding: 0 1rem;
    }

    .inner {
      @include flex-center-sb;
      gap: 2rem;
      padding: 2rem;
      box-shadow: map-get($shadows, 'Elevation 3');

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        @include flex-stretch-sb;
        flex-direction: column;
        gap: 1rem;
        padding: 1.5rem 1rem;
      }

      &:deep(.left) {
        h4 {
          @include h4;
          margin-bottom: 1rem;
        }

        strong {
          @include subtitle-1;
        }
      }
    }
  }
}
</style>
